import React, { useMemo } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Box, Button, ButtonGroup, Divider, Stack, Typography, DateTimeStandard } from "@curves/ui-base";
import { Overview } from "@curves/ui-shared";
import { parseDate } from "@util/moment";
import { ensureArray } from "@util/utils";
import { CLUB_RECORD_REFERENCE_TYPE, INVENTORY_RECORD_TYPE } from "@clubInventory/constants/dataEnums";

// 功能：異動紀錄表格欄位定義
const headerRow = [
	{
		cellKey: "recordDate",
		headerLabel: "庫存異動時間",
		formatCell: (value, row) => {
			if (!value) return "—";

			const referenceType = row?.referenceType;
			if (Number(referenceType) === CLUB_RECORD_REFERENCE_TYPE.POSSALE) {
				// PosSale：同一行顯示日期時間
				return parseDate(value, DateTimeStandard.DisplayFullBySlash);
			}

			// 其他：兩行顯示日期 + 時間（排版參考圖片）
			const dateText = parseDate(value, DateTimeStandard.DisplayDateBySlash);
			const timeText = parseDate(value, DateTimeStandard.DisplayTime);
			return (
				<Box>
					<Typography component="div" variant="body2">
						{dateText}
					</Typography>
					<Typography component="div" variant="body2" className="font-color-3">
						{timeText}
					</Typography>
				</Box>
			);
		},
	},
	{ cellKey: "referenceTypeText", headerLabel: "類型", minWidth: 100 },
	{
		cellKey: "quantity",
		headerLabel: "數量",
		minWidth: 80,
		formatCell: value => (
			<Typography component="span" color={value > 0 ? "success" : value < 0 ? "error" : ""}>
				{value > 0 ? `+${value}` : value}
			</Typography>
		),
	},
	{ cellKey: "employeeName", headerLabel: "擔當", minWidth: 100 },
	{ cellKey: "notes", headerLabel: "原因", minWidth: 200 },
];

// 功能：摘要資料預設值（API 尚未回傳時的初始狀態）
const defaultSummary = {
	originalTotal: 0,
	inbound: 0,
	outbound: 0,
	totalInventory: 0,
};

// 功能：recordType 對應要套用模糊效果的摘要卡（全部則不模糊）
const SUMMARY_TYPE_BY_RECORD_TYPE = {
	[INVENTORY_RECORD_TYPE.INBOUND]: "inbound",
	[INVENTORY_RECORD_TYPE.OUTBOUND]: "outbound",
};

// 功能：異動紀錄（含分頁）
// 上方：最近盤點日期、區間切換按鈕、庫存量變化摘要；下方：表格
const StockChangeHistory = ({
	changeHistoryOverviewData,
	updateChangeHistoryPage,
	className = "",
	lastInventoryDate,
	summary = defaultSummary,
	recordRangeOptions,
	recordRange,
	onRecordRangeChange,
	recordType = INVENTORY_RECORD_TYPE.ALL,
	onRecordTypeChange,
}) => {
	const { originalTotal, inbound, outbound, totalInventory } = summary;

	// 模糊效果依 recordType 推導（入庫→inbound、出庫→outbound、全部→不模糊）
	const activeSummaryType = SUMMARY_TYPE_BY_RECORD_TYPE[recordType] ?? null;

	// recordRange 型別統一
	const currentRangeValue = recordRange != null ? Number(recordRange) : 1;

	// 進貨/出貨啟用 blur；點總庫存恢復
	const isBlurEnabled = activeSummaryType === "inbound" || activeSummaryType === "outbound";
	const getSummaryItemClassName = (itemType, typeClassName) =>
		clsx(
			"stock-change-history-summary-item",
			{
				"is-blur": isBlurEnabled && itemType !== activeSummaryType,
			},
			(!isBlurEnabled || itemType === activeSummaryType) && typeClassName,
		);

	// 摘要卡點擊事件：切換 recordType 觸發後端重新查詢（換頁由父層 useEffect 重置回第 1 頁）
	const handleClickInbound = () => onRecordTypeChange?.(INVENTORY_RECORD_TYPE.INBOUND);
	const handleClickOutbound = () => onRecordTypeChange?.(INVENTORY_RECORD_TYPE.OUTBOUND);
	const handleClickTotal = () => onRecordTypeChange?.(INVENTORY_RECORD_TYPE.ALL);

	// Overview context
	const contextValue = useMemo(() => ({ overviewData: changeHistoryOverviewData, updateOverviewPage: updateChangeHistoryPage }), [changeHistoryOverviewData, updateChangeHistoryPage]);

	return (
		<Box className={clsx("stock-change-history", className)}>
			{/* 頂列：最近盤點日期（左） + 區間切換按鈕（右） */}
			<Stack direction="row" justifyContent="end" alignItems="center" className="stock-change-history-header mb-3">
				<Box className="stock-change-history-date-pill">
					<Typography variant="body2" component="span" className="stock-change-history-date-label">
						最近盤點日期
					</Typography>
					<Typography variant="body1" component="span" className="stock-change-history-date-value">
						{lastInventoryDate ? parseDate(lastInventoryDate, DateTimeStandard.DisplayDateBySlash) : "—"}
					</Typography>
				</Box>
				<ButtonGroup variant="outlined" size="small" className="stock-change-history-filter-group">
					{ensureArray(recordRangeOptions).map(opt => {
						const value = Number(opt.value);
						return (
							<Button
								key={value}
								className={clsx({ "stock-change-history-filter-active": currentRangeValue === value })}
								onClick={() => onRecordRangeChange && onRecordRangeChange(value)}
							>
								{opt.text}
							</Button>
						);
					})}
				</ButtonGroup>
			</Stack>

			{/* 庫存量變化摘要：原總數 ＋ 進貨 － 出貨 ＝ 總庫存數 */}
			<Box className="stock-change-history-summary">
				<Box className={getSummaryItemClassName("original", "stock-change-history-summary-original")}>
					<Typography variant="body2" className="stock-change-history-summary-label">
						原總數
					</Typography>
					<Typography variant="body1" className="stock-change-history-summary-value">
						{originalTotal}
					</Typography>
				</Box>
				<Box className={getSummaryItemClassName("inbound", "stock-change-history-summary-inbound")} onClick={handleClickInbound}>
					<Typography variant="body2" className="stock-change-history-summary-label">
						進貨
					</Typography>
					<Typography variant="body1" className="stock-change-history-summary-value stock-change-history-summary-value-inbound">
						{inbound >= 0 ? `+${inbound}` : inbound}
					</Typography>
				</Box>
				<Box className={getSummaryItemClassName("outbound", "stock-change-history-summary-outbound")} onClick={handleClickOutbound}>
					<Typography variant="body2" className="stock-change-history-summary-label">
						出貨
					</Typography>
					<Typography variant="body1" className="stock-change-history-summary-value stock-change-history-summary-value-outbound">
						{outbound <= 0 ? outbound : `-${outbound}`}
					</Typography>
				</Box>
				<Typography component="span" className="stock-change-history-summary-op">
					=
				</Typography>
				<Box className={getSummaryItemClassName("total", "stock-change-history-summary-total")} onClick={handleClickTotal}>
					<Typography variant="body2" className="stock-change-history-summary-label">
						總庫存數
					</Typography>
					<Typography variant="body1" className="stock-change-history-summary-value">
						{totalInventory}
					</Typography>
				</Box>
			</Box>

			<Divider className="my-2" />

			{/* 異動紀錄表格（含筆數、分頁） */}
			<Overview contextValue={contextValue} idKey="clubRecordID">
				<Overview.TotalCount className="text-right mb-2" showCurrentCount />
				<Overview.Table headerRow={headerRow} tableClassName="theme-gray" className="mb-3" />
				<Overview.Pagination />
			</Overview>
		</Box>
	);
};

StockChangeHistory.propTypes = {
	changeHistoryOverviewData: PropTypes.shape({
		list: PropTypes.array,
		pagination: PropTypes.shape({
			pageIndex: PropTypes.number,
			totalPage: PropTypes.number,
			totalCount: PropTypes.number,
		}),
	}),
	updateChangeHistoryPage: PropTypes.func,
	className: PropTypes.string,
	lastInventoryDate: PropTypes.string,
	summary: PropTypes.shape({
		originalTotal: PropTypes.number,
		inbound: PropTypes.number,
		outbound: PropTypes.number,
		totalInventory: PropTypes.number,
	}),
	recordRangeOptions: PropTypes.array,
	recordRange: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	onRecordRangeChange: PropTypes.func,
	recordType: PropTypes.number,
	onRecordTypeChange: PropTypes.func,
};

export default StockChangeHistory;
export { headerRow as stockChangeHistoryHeaderRow };
